// Shared types, utilities, and API helpers for the ProfileModal component.

import type { ServerErrorBody } from '@auctogon/ui/types';
import type { StaffNotificationPreferenceDto } from '@auctogon/core-admin/schemas/responses/staff-notification-preferences';

import { api } from '../../../api-client/typed-client';
import type { AuthOps } from '../../../api-client/auth-ops';
import { formatPhoneDisplay, formatPhoneE164 } from '@auctogon/core/validation/phone';

/** Tab identifiers for the profile modal navigation. */
export type ProfileTab = 'profile' | 'security' | 'notifications';

/** Shape of the editable user profile fields. */
export interface UserProfile {
  /** The captured given name — a required field on this form (BUG-1650 AC 3). */
  firstName: string;
  /** The captured family name — likewise required on this form. */
  lastName: string;
  /**
   * The server-resolved display string, for the modal header only. Read-only
   * here: it is DERIVED from the parts, so the form edits those and never this.
   */
  name: string;
  email: string;
  phone: string;
}

/** Password fields for the security tab form. */
export interface PasswordFields {
  current: string;
  new: string;
  confirm: string;
}

/** Visibility toggles for each password input. */
export interface PasswordVisibility {
  current: boolean;
  new: boolean;
  confirm: boolean;
}

/** Tab configuration for the profile modal navigation. */
export const PROFILE_TABS: { id: ProfileTab; label: string }[] = [
  { id: 'profile', label: 'Profile' },
  { id: 'security', label: 'Security' },
  { id: 'notifications', label: 'Notifications' },
];

/* ------------------------------------------------------------------ */
/*  API helpers                                                        */
/* ------------------------------------------------------------------ */

/** Minimal user shape from the auth context, used by data-loading helpers. */
export interface AuthUser {
  id: string;
  email?: string;
  user_metadata?: Record<string, string>;
}

/**
 * Build a UserProfile from auth session metadata (fallback when API fails).
 * @param user - The authenticated user from the auth context
 * @returns A populated UserProfile
 */
export function buildProfileFromSession(user: AuthUser): UserProfile {
  // BUG-1650: NO email local-part fallback. This value is pre-filled into the
  // Full Name box, and saving the form persisted it — which is how an email
  // address became a staff member's real stored name (the same defect BUG-1154
  // closed on the invite-enrollment side). The session carries no name parts,
  // so the fields open empty and the form's own `required` rules ask the
  // operator for a genuine name.
  const phone = user.user_metadata?.phone || '';
  return {
    firstName: '',
    lastName: '',
    name: user.user_metadata?.name || user.user_metadata?.full_name || '',
    email: user.email || '',
    phone: phone ? formatPhoneDisplay(phone) : '',
  };
}

/**
 * Fetch the signed-in user's own profile from the self-service endpoint.
 * Keyed server-side on the session's WorkOS id, so the verified phone stored
 * on `public.users` is loaded directly (BUG-630). Falls back to session data
 * if the API call fails.
 * @param user - The authenticated user from the auth context (for fallback)
 * @returns A populated UserProfile
 */
export async function fetchUserProfile(user: AuthUser): Promise<UserProfile> {
  try {
    // Bespoke envelope: route returns { success, profile } at the top level
    // (not under .data), and we read response.ok to fall back to session
    // identity on a 502/504 HTML body — so use the typed client's .raw() escape
    // hatch, which returns the raw Response.
    const response = await api.user.profile.get.raw();
    // Non-OK (e.g. an HTML 502/504 gateway page) would throw in .json(); fall
    // back to session identity gracefully rather than crash the modal load.
    if (!response.ok) return buildProfileFromSession(user);
    const result = await response.json();

    if (result.success && result.profile) {
      // BUG-1650: the route returns the name PARTS (the editable truth) plus the
      // already-resolved display string for the header. No local-part fallback
      // anywhere — see buildProfileFromSession above for why that mattered.
      const { firstName, lastName, name, email, phone } = result.profile as {
        firstName?: string | null;
        lastName?: string | null;
        name?: string;
        email?: string;
        phone?: string;
      };
      return {
        firstName: firstName ?? '',
        lastName: lastName ?? '',
        name: name ?? '',
        email: email || user.email || '',
        phone: phone ? formatPhoneDisplay(phone) : '',
      };
    }
  } catch (err) {
    console.error('Failed to load user data:', err);
  }
  return buildProfileFromSession(user);
}

/**
 * Save the signed-in user's own name + phone via the self-service endpoint.
 * The server keys the update on the session's WorkOS id — no userId is sent in
 * the body (sending the WorkOS id to /api/user/update was what triggered the
 * uuid-cast 500 in BUG-630).
 * @param profile - The current profile form values
 * @returns The saved profile, or the server's error envelope (BUG-2567: with its
 *   `fieldErrors`, so the form can show them under the fields they name)
 * @throws Error on a network failure only
 */
export async function saveProfileToServer(
  profile: Pick<UserProfile, 'firstName' | 'lastName' | 'phone'>
): Promise<{ success: true; profile?: UserProfile } | ServerErrorBody> {
  const formattedPhone = profile.phone ? formatPhoneE164(profile.phone) : '';
  // Bespoke envelope: route returns { success, profile } at the top level (not
  // under .data), and we read response.ok separately to distinguish a non-JSON
  // 502/504 body — so use the typed client's .raw() escape hatch, which returns
  // the raw Response.
  //
  // BUG-1650: the PARTS go over the wire, never a composed `name`. The route's
  // schema is `.strict()`, so a stale client sending `name` gets a 400 naming
  // the field instead of the silent 200 that used to write the wrong column.
  const response = await api.user.profile.patch.raw({
    body: {
      firstName: profile.firstName,
      lastName: profile.lastName,
      phone: formattedPhone,
    },
  });

  // Guard against a non-JSON error body (HTML 502/504) throwing in .json().
  const result = await response.json().catch(() => null);
  if (!response.ok || !result?.success) {
    return {
      success: false,
      error: result?.error || 'Failed to update profile',
      ...(result?.fieldErrors ? { fieldErrors: result.fieldErrors } : {}),
    };
  }
  return result;
}

/**
 * Submit a password change request via Better Auth (BL-788).
 *
 * The staff auth cutover (BL-787B) moved sign-in onto Better Auth, but this
 * helper still hit the legacy WorkOS `/api/auth/change-password` route — which,
 * post-cutover, only read the never-set `WORKOS_SESSION_COOKIE` and always 401'd
 * (the same route-shadow class as the #3040 reset-password fix). It now calls
 * Better Auth's client `changePassword` (served by the `/api/auth/[...all]`
 * catch-all), mirroring bidder-web's `useUserProfile.changePassword`.
 *
 * Password-credential staff (email + password sign-in) get a working change.
 * Google-OAuth / email-OTP-only staff have no password credential — Better Auth
 * rejects those with an error the caller surfaces inline; the empty-state polish
 * (hiding the Security tab for password-less staff) is BL-903.
 *
 * `changePassword` is injected (BL-1600) rather than imported: the Better Auth
 * client is typed from the SERVER auth instance and so stays in
 * `apps/admin-web` — see `api-client/auth-ops.ts`. Options object because this
 * takes three parameters (company-standards.md § "Function Design Rules").
 *
 * @param input.currentPassword - The user's current password
 * @param input.newPassword - The desired new password
 * @param input.changePassword - The shell-supplied Better Auth operation
 * @throws Error if Better Auth reports failure
 * @sideEffect Writes the new credential via Better Auth.
 */
export async function changePasswordOnServer(input: {
  currentPassword: string;
  newPassword: string;
  changePassword: AuthOps['changePassword'];
}): Promise<void> {
  const { currentPassword, newPassword, changePassword } = input;
  const { error } = await changePassword({ currentPassword, newPassword });
  if (error) throw new Error(error.message ?? 'Failed to update password');
}

/**
 * Load the signed-in staff user's own notification switches at `tenantId`
 * (BL-1230).
 *
 * The response is eligibility-filtered server-side, so an empty `preferences`
 * array is a legitimate answer ("your role has no optional alerts"), not an
 * error — callers render the empty state rather than treating it as a failure.
 *
 * Response type is inferred from the registry (`responseType<…>()`, ADR 027) —
 * no caller-asserted `<T>`.
 *
 * @param tenantId - the active tenant. Preferences are per tenant (BL-1230 AC 7).
 * @returns the eligible preferences with their current settings.
 * @throws Error when the request fails, so the modal can surface it inline.
 */
export async function fetchNotificationPreferences(
  tenantId: string,
): Promise<StaffNotificationPreferenceDto[]> {
  const body = await api.user.notification_preferences.get({ query: { tenantId } });
  if (!body.success) {
    throw new Error(body.error || 'Failed to load notification settings');
  }
  return body.data.preferences;
}

/**
 * Save the full set of notification switches for `tenantId`.
 *
 * One request, committed server-side in one transaction (ADR 026 rule 1) — the
 * client never fans a multi-toggle save out into a call per toggle. The response
 * carries the canonical post-write state, so the caller re-renders from it
 * instead of firing a follow-up GET.
 *
 * @param tenantId - the active tenant.
 * @param preferences - the desired state of every eligible toggle.
 * @returns the canonical post-save preferences.
 * @sideEffect Upserts the signed-in user's `staff_notification_opt_ins` preference rows.
 * @throws Error when the request fails.
 */
export async function saveNotificationPreferences(
  tenantId: string,
  preferences: StaffNotificationPreferenceDto[],
): Promise<StaffNotificationPreferenceDto[]> {
  const body = await api.user.notification_preferences.put({
    query: { tenantId },
    body: {
      // A locked row is not the viewer's to save; the server would refuse the whole
      // request with NOT_ELIGIBLE (BUG-2272).
      preferences: preferences
        .filter((p) => !p.locked)
        .map((p) => ({
          eventType: p.eventType,
          channel: p.channel,
          enabled: p.enabled,
        })),
    },
  });
  if (!body.success) {
    throw new Error(body.error || 'Failed to save notification settings');
  }
  return body.data.preferences;
}
